/**
 * InDeckSection - Quantity and zone controls for cards in deck
 *
 * Displays and allows editing of quantity and zone for a card that's in the deck.
 * Only shown when viewing a card in 'in-deck' mode.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { FaCrown, FaExchangeAlt, FaMinus, FaPlus, FaTrash } from 'react-icons/fa';

import useDeckCardOperations from '@/features/decks/hooks/useDeckCardOperations';
import { DECK_ZONE_LABELS } from '@/lib/deckUtils';
import { canMoveToZone, getAvailableZonesForCard } from '@/lib/deckValidation';
import type { DeckFormat, DeckStatus, DeckZone } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

export interface InDeckSectionProps {
  /** Deck card document ID */
  deckCardId: string;
  /** Deck ID */
  deckId: string;
  /** Current quantity */
  quantity: number;
  /** Current zone */
  zone: DeckZone;
  /** Deck format (affects available zones) */
  format: DeckFormat | null;
  /** Deck status (affects maybeboard availability) */
  status: DeckStatus;
  /** Card data for quantity limits and commander eligibility */
  card?: { name: string; type_line?: string | null; oracle_text?: string | null } | null;
  /** All cards in the deck - required for commander zone handling */
  allCards?: { id: string; zone: string; scryfall_id: string }[];
  /** Whether deck is locked */
  isLocked?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

const InDeckSection = memo(function InDeckSection({
  deckCardId,
  deckId,
  quantity,
  zone,
  format,
  status,
  card,
  allCards,
  isLocked = false,
  className,
}: InDeckSectionProps): ReactElement {
  const { increase, decrease, changeZone, remove, canIncrease, isLoading } = useDeckCardOperations({
    deckId,
    isLocked,
    deckFormat: format,
    allCards,
  });
  const [showZoneDropdown, setShowZoneDropdown] = useState(false);

  const canModify = !isLocked && !isLoading;
  const canAdd = canModify && card && canIncrease(card, quantity);

  // Determine available zones using zone rules
  const availableZones = useMemo((): DeckZone[] => {
    if (!card) return ['main'];
    return getAvailableZonesForCard(card, format, status);
  }, [format, status, card]);

  // Handle quantity changes
  const handleIncrease = useCallback(() => {
    increase(deckCardId, quantity);
  }, [increase, deckCardId, quantity]);

  const handleDecrease = useCallback(() => {
    decrease(deckCardId, quantity);
  }, [decrease, deckCardId, quantity]);

  // Handle zone change with validation
  const handleZoneChange = useCallback(
    (newZone: DeckZone) => {
      if (newZone === zone) {
        setShowZoneDropdown(false);
        return;
      }

      // Validate the zone change
      if (card) {
        const validation = canMoveToZone(card, newZone, format, status);
        if (!validation.allowed) {
          console.warn('Zone change blocked:', validation.reason);
          setShowZoneDropdown(false);
          return;
        }
      }

      changeZone(deckCardId, newZone);
      setShowZoneDropdown(false);
    },
    [changeZone, deckCardId, zone, card, format, status],
  );

  // Handle remove
  const handleRemove = useCallback(() => {
    remove(deckCardId);
  }, [remove, deckCardId]);

  return (
    <section className={clsx('px-6 pb-6', className)}>
      <div className="bg-slate/50 border-storm rounded-xl border p-5">
        {/* Section Header */}
        <h3 className="text-ash mb-4 text-xs font-medium tracking-widest uppercase">In This Deck</h3>

        {/* Quantity Controls */}
        <div className="flex items-center justify-between">
          <span className="text-silver text-sm">Quantity</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleDecrease}
              disabled={!canModify || quantity <= 1}
              className={clsx(
                'bg-slate border-storm text-silver hover:text-pearl flex h-8 w-8 items-center justify-center rounded-lg border transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
              aria-label="Decrease quantity"
            >
              <FaMinus className="h-3 w-3" />
            </button>
            <span className="text-pearl min-w-10 text-center font-mono text-lg font-medium">{quantity}</span>
            <button
              type="button"
              onClick={handleIncrease}
              disabled={!canAdd}
              className={clsx(
                'bg-slate border-storm text-silver hover:text-pearl flex h-8 w-8 items-center justify-center rounded-lg border transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
              aria-label="Increase quantity"
            >
              <FaPlus className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Zone Selector */}
        <div className="mt-4 flex items-center justify-between">
          <span className="text-silver text-sm">Zone</span>
          <div className="relative">
            <button
              type="button"
              onClick={() => !isLocked && setShowZoneDropdown(!showZoneDropdown)}
              disabled={isLocked}
              className={clsx(
                'flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                'bg-slate border-storm border',
                isLocked ? 'cursor-not-allowed opacity-50' : 'hover:bg-storm',
              )}
            >
              {zone === 'commander' && <FaCrown className="h-3 w-3 text-amber-400" />}
              <span className="text-pearl">{DECK_ZONE_LABELS[zone]}</span>
              {!isLocked && <FaExchangeAlt className="text-ash h-3 w-3" />}
            </button>

            {showZoneDropdown && (
              <>
                {/* Backdrop */}
                <div className="fixed inset-0 z-10" onClick={() => setShowZoneDropdown(false)} />
                {/* Dropdown */}
                <div className="absolute right-0 z-20 mt-1 w-36 rounded-lg border border-zinc-700 bg-zinc-800 py-1 shadow-lg">
                  {availableZones.map((z) => (
                    <button
                      key={z}
                      type="button"
                      onClick={() => handleZoneChange(z)}
                      className={clsx(
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                        z === zone ? 'bg-zinc-700 text-zinc-200' : 'text-zinc-400 hover:bg-zinc-700/50 hover:text-zinc-200',
                      )}
                    >
                      {z === 'commander' && <FaCrown className="h-3 w-3 text-amber-400" />}
                      <span>{DECK_ZONE_LABELS[z]}</span>
                      {z === zone && <span className="ml-auto text-xs text-zinc-500">✓</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Remove Button */}
        {!isLocked && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={isLoading}
            className={clsx(
              'mt-4 flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
              'text-danger/80 hover:text-danger hover:bg-danger/10 border-danger/30 border',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <FaTrash className="h-3 w-3" />
            <span>Remove from Deck</span>
          </button>
        )}
      </div>
    </section>
  );
});

export default InDeckSection;
